"use client";

import type { ToolUIPart } from "ai";
import type { ComponentProps, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ToolUIPartApproval =
  | {
      id: string;
      approved?: never;
      reason?: never;
    }
  | {
      id: string;
      approved: boolean;
      reason?: string;
    }
  | undefined;

type ConfirmationContextValue = {
  approval: ToolUIPartApproval;
  state: ToolUIPart["state"];
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(
  null,
);

function useConfirmation(): ConfirmationContextValue {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("Confirmation components must be used within Confirmation");
  }
  return context;
}

/** 共通 confirmation surface に必要な AI SDK の承認状態。 */
export type ConfirmationProps = ComponentProps<typeof Alert> & {
  approval?: ToolUIPartApproval;
  state: ToolUIPart["state"];
};

/**
 * AI Elements の tool approval 状態を Alert として描画します。
 * approval が存在しない入力生成中の状態は描画しません。
 */
export function Confirmation({
  className,
  approval,
  state,
  ...props
}: ConfirmationProps) {
  const contextValue = useMemo(() => ({ approval, state }), [approval, state]);

  if (!approval || state === "input-streaming" || state === "input-available") {
    return null;
  }

  return (
    <ConfirmationContext.Provider value={contextValue}>
      <Alert className={cn("gap-y-2", className)} {...props} />
    </ConfirmationContext.Provider>
  );
}

/** confirmation の状態説明を表示する title の props。 */
export type ConfirmationTitleProps = ComponentProps<typeof AlertDescription>;

/** confirmation card 内で状態説明を一貫した typography で表示する。 */
export function ConfirmationTitle({
  className,
  ...props
}: ConfirmationTitleProps) {
  return <AlertDescription className={cn("inline", className)} {...props} />;
}

/** 承認待ちの間だけ表示する内容。 */
export type ConfirmationRequestProps = {
  children?: ReactNode;
};

/** request 状態のときだけ children を描画し、応答後の重複表示を防ぐ。 */
export function ConfirmationRequest({ children }: ConfirmationRequestProps) {
  return useConfirmation().state === "approval-requested" ? children : null;
}

/** 承認済みの応答・実行結果で表示する内容。 */
export type ConfirmationAcceptedProps = {
  children?: ReactNode;
};

/** 承認後から terminal state まで、承認済み表示を維持する。 */
export function ConfirmationAccepted({ children }: ConfirmationAcceptedProps) {
  const { approval, state } = useConfirmation();
  if (
    !approval?.approved ||
    ![
      "approval-responded",
      "output-denied",
      "output-available",
      "output-error",
    ].includes(state)
  ) {
    return null;
  }
  return children;
}

/** 拒否済みの応答・実行結果で表示する内容。 */
export type ConfirmationRejectedProps = {
  children?: ReactNode;
};

/** 拒否後から terminal state まで、拒否済み表示を維持する。 */
export function ConfirmationRejected({ children }: ConfirmationRejectedProps) {
  const { approval, state } = useConfirmation();
  if (
    approval?.approved !== false ||
    ![
      "approval-responded",
      "output-denied",
      "output-available",
      "output-error",
    ].includes(state)
  ) {
    return null;
  }
  return children;
}

/** 承認待ちの操作領域に渡す layout props。 */
export type ConfirmationActionsProps = ComponentProps<"div">;

/**
 * shadcn/ui の Dialog footer と同じ順序・配置で承認操作を並べます。
 * 狭い画面では主操作を上にした縦並び、十分な幅では右寄せの横並びにします。
 */
export function ConfirmationActions({
  className,
  ...props
}: ConfirmationActionsProps) {
  if (useConfirmation().state !== "approval-requested") {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 self-end sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

/** 共通 confirmation button に渡す props。 */
export type ConfirmationActionProps = ComponentProps<typeof Button>;

/** 承認操作を共通 Button の標準サイズで描画し、操作間の高さを揃えます。 */
export function ConfirmationAction(props: ConfirmationActionProps) {
  return <Button type="button" {...props} />;
}
